import React, { useMemo } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ImageSlider from "../../../components/common/CampaignDetailPageComponents/ImageSlider";

// Helper function to build full image URL from relative path
const buildImageUrl = (imagePath) => {
  if (!imagePath) return '';
  // If already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  // If relative path, prepend base URL if available
  const imageBase = import.meta.env.VITE_API_IMAGE_PATH || import.meta.env.VITE_API_BASE_URL || '';
  if (imageBase && !imagePath.startsWith('/')) {
    return `${imageBase}/${imagePath}`;
  } else if (imageBase) {
    return `${imageBase}${imagePath}`;
  }
  return imagePath;
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to parse description into content array
const closingKeywords = [
  "regards",
  "warm regards",
  "thanks & regards",
  "thanks and regards",
  "best regards",
  "best wishes",
  "sincerely",
  "yours truly",
];

const parseDescription = (description) => {
  if (!description) return [];
  
  const content = [];
  const paragraphs = description.split('\r\n\r\n').filter(p => p.trim());
  let awaitingSignature = false;
  
  paragraphs.forEach((para, index) => {
    if (index === 0 && para.includes('Dear')) {
      // First paragraph might be a title
      content.push({ type: "title", text: para.trim() });
    } else {
      // Regular paragraphs
      const lines = para.split('\r\n').filter(l => l.trim());
      lines.forEach(line => {
        const trimmedLine = line.trim();
        if (!trimmedLine) return;

        const normalized = trimmedLine.replace(/[.,]/g, '').toLowerCase();

        if (closingKeywords.some(keyword => normalized === keyword)) {
          content.push({ type: "closing", text: trimmedLine });
          awaitingSignature = true;
          return;
        }

        if (awaitingSignature) {
          content.push({ type: "signature", text: trimmedLine });
          awaitingSignature = false;
          return;
        }

        content.push({ type: "paragraph", text: trimmedLine });
      });
    }
  });
  
  return content.length > 0 ? content : [{ type: "paragraph", text: description }];
};

const UpdateSection = ({ donationData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const isAbove600px = useMediaQuery("(min-width: 600px)");

  const updatesData = useMemo(() => {
    // Check for updates array from API
    if (donationData?.updates && Array.isArray(donationData.updates) && donationData.updates.length > 0) {
      return donationData.updates
        .filter(update => update.status === 0 || update.status === false) // Only show active updates (status 0)
        .map((update, index) => {
          // Extract image URLs from images array (handle both string URLs and objects with url property)
          // Use buildImageUrl like campaign component does
          let imageUrls = [];
          if (Array.isArray(update.images) && update.images.length > 0) {
            imageUrls = update.images.map(img => {
              if (typeof img === 'string') {
                // Direct URL string
                return buildImageUrl(img);
              } else if (img && typeof img === 'object') {
                // Handle object with url property (e.g., { id: 123, url: "uploads/..." })
                if (img.url && typeof img.url === 'string') {
                  return buildImageUrl(img.url);
                }
                return '';
              }
              return '';
            }).filter(img => img && img !== ''); // Remove empty strings
          }

          return {
            ...update,
            id: update.id || index + 1,
            date: update.date || update.updatedAt || update.createdAt || "",
            images: imageUrls,
            content: Array.isArray(update.content) && update.content.length > 0
              ? update.content
              : parseDescription(update.description || ""),
          };
        });
    }

    // Fallback: Check for monthly_media_uploads (legacy support)
    if (donationData?.monthly_media_uploads && Array.isArray(donationData.monthly_media_uploads) && donationData.monthly_media_uploads.length > 0) {
      const allImages = donationData.monthly_media_uploads
        .map(img => {
          if (typeof img === 'string') {
            return buildImageUrl(img);
          } else if (img && typeof img === 'object' && img.url) {
            return buildImageUrl(img.url);
          }
          return '';
        })
        .filter(img => img && img !== '');
      if (allImages.length > 0) {
        return [{
          id: 1,
          date: donationData.updatedAt || donationData.createdAt || "",
          images: allImages,
          content: parseDescription(donationData.description || "") || [
            { type: "title", text: "Dear Donors," },
            { type: "paragraph", text: donationData.description || "" },
            { type: "closing", text: "Regards," },
            { type: "signature", text: "True Hope Foundation." },
          ],
        }];
      }
    }

    return [];
  }, [donationData?.updates, donationData?.monthly_media_uploads, donationData?.description, donationData?.updatedAt, donationData?.createdAt]);

  if (!updatesData || updatesData.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        // maxWidth: "1200px",
        mx: "auto",
        p: isMobile ? "0px" : "30px",
        border: isMobile ? "none" : "1px solid #F1F1F1",
        borderRadius: "12px",
        width: "100%",
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          fontSize: isMobile ? "12px" : "14px",
          color: "#131313",
          fontWeight: 400,
          letterSpacing: "0.1em",
          mb: isMobile ? "10px" : "20px",
          textTransform: "uppercase",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          component="span"
          sx={{
            color: "#A9C74E",
            fontSize: "20px",
            lineHeight: 1,
          }}
        >
          ●
        </Box>
        Updates
      </Typography>

      {/* Render each update */}
      {updatesData.map((update, index) => {
        const updateNumber = index + 1;
        const updateDate =
          update.formattedDate ||
          (update.date && update.date.includes("/") ? update.date : formatDate(update.date)) ||
          "";
        const contentItems =
          Array.isArray(update.content) && update.content.length > 0
            ? update.content
            : parseDescription(update.description || "");

        return (
        <Box
          key={updateNumber}
          sx={{
            mb:
              index < updatesData.length - 1
                ? isMobile
                  ? "12px"
                  : "30px"
                : "0px",
            pb:
              index < updatesData.length - 1
                ? isMobile
                  ? "20px"
                  : "30px"
                : "0px",
            borderBottom:
              index < updatesData.length - 1 ? "1px solid #B9B9B8" : "0px",
          }}
        >
          {/* Update Header */}
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: isMobile ? "10px" : "14px",
              fontWeight: 700,
              color: "#151513",
              mb: isMobile ? "14px" : "30px",
              backgroundColor: "#F5F8ED",
              padding: isMobile ? "7px 10px" : "16px",
              borderRadius: isMobile ? "3px" : "8px",
            }}
          >
            <Box
              component="span"
              sx={{
                borderRight: "0.5px solid #B9B9B8",
                paddingRight: "6px",
                marginRight: "6px",
              }}
            >
              #{updateNumber} -
            </Box>
            Updated on{" "}
            <Box component="span" sx={{ color: "#8EA946", fontWeight: "800" }}>
              {updateDate}
            </Box>
          </Typography>

          {/* Content Layout */}
          <Box
          className="update-content"
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? "14px" :"20px",
              alignItems: "flex-start",
            }}
          >
             {/* Images Section */}
             {update.images && update.images.length > 0 && (
               <Box
                 sx={{
                   width: "100%",
                   maxWidth: isAbove600px ? "400px" : "100%",
                   height: isMobile ? "auto" : "385px",
                   "@media (min-width: 1600px)": {
                     height: "320px",
                   },
                   flex: isMobile ? "none" : isAbove600px ? "0 0 400px" : "1",
                 }}
               >
                <ImageSlider
                  images={update.images}
                  settings={{
                    dots: true,
                    infinite: true,
                    speed: 3000,
                    slidesToShow: 1, 
                    slidesToScroll: 1,
                    autoplay: true,
                    arrows: false,
                    centerMode: isMobile ? true : false,
                    centerPadding: isMobile ? "30px" : "0px",
                  }}
                   imgSx={{
                     width: isMobile ? "280px" : "100%",
                     height: isMobile ? "180px" : "385px",
                     "@media (min-width: 1600px)": {
                       height: "320px",
                     },
                     objectFit: "cover",
                     borderRadius: "8px",
                   }}
                  noPaddingRight={true}
                />
              </Box>
            )}

            {/* Text Content */}
            <Box sx={{ flex: update.images?.length > 0 ? "1" : "100%" }}>
              {contentItems.map((item, pIndex) => {
                let styles = {
                  fontSize: isMobile ? "12px" : isLaptop ? "12px" : "14px",
                  lineHeight: "24px",
                  color: "#1D1D1B",
                  mb: isMobile ? "8px" : isLaptop ? "10px" : "14px",
                  fontWeight: isMobile ? "400" : "500",
                };

                if (item.type === "title") {
                  styles = {
                    ...styles,
                    fontWeight: 700,
                    mb: isLaptop ? "8px" : "20px",
                  };
                }
                if (item.type === "closing") {
                  styles = { ...styles, fontWeight: 600, mb: "0" };
                }
                if (item.type === "signature") {
                  styles = { ...styles, fontWeight: 700, mb: "0" };
                }

                return (
                  <Typography key={pIndex} sx={styles}>
                    {item.text}
                  </Typography>
                );
              })}
            </Box>
          </Box>
        </Box>
      )})}
    </Box>
  );
};

export default UpdateSection;
